import { Fragment, useMemo, useState } from "react";
import { Button, ButtonGroup, Dropdown, Modal } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { DateTimeInput } from "../../../components/forms";
import { GenericAdviserSelect } from "../../../components/forms/selects";
import { Spinner } from "../../../components/loaders";
import { ErrorMessage, ErrorMessageWrapper } from "../../../components/messages";
import { ViewPdfModal } from "../../../components/modals";
import { ClientLayoutPageSubHeader } from "../../../components/utilities";
import { toNString } from "../../../helpers/guids";
import { useClientContext } from "../../../hooks/ClientContext";
import useArchiveFactFind from "../hooks/useArchiveFactFind";
import useFactFindPdf from "../hooks/useFactFindPdf";
import useVerifyFactFind from "../hooks/useVerifyFactFind";
import ThemedModal from "../../../components/utilities/ThemedModal";

const FactFindAdminButton = ({
    hasAccess,
    hasMenuAccess,
    onClick,
    onAdviserClick,
    disabled,
    variant,
    isFetchingStatus,
    isUpdatingStatus,
    label,
    action,
    info
}) => {
    if (!hasAccess)
        return <span className="me-2 my-auto">
            {info}
        </span>

    const buttonLabel = isFetchingStatus || isUpdatingStatus
        ? <Spinner />
        : <p className="m-0">
            {label}
            <br />
            <span className="fs-smallest">
                {info}
            </span>
        </p>

    if (!hasMenuAccess)
        return <Button onClick={onClick} disabled={disabled} variant={variant}>
            {buttonLabel}
        </Button>;

    return <Dropdown as={Button} className="caret-off p-0" disabled={disabled} variant={variant}>
        <Dropdown.Toggle as={'span'} className="d-flex text-center px-2 py-2 h-100 w-100 align-items-center">
            {buttonLabel}
        </Dropdown.Toggle>
        <Dropdown.Menu>
            <Dropdown.Header>{action}</Dropdown.Header>
            <Dropdown.Item onClick={onClick}>{action} as Me</Dropdown.Item>
            <Dropdown.Item onClick={onAdviserClick}>{action} as Adviser</Dropdown.Item>
        </Dropdown.Menu>
    </Dropdown>
}

const FactFindAdminModal = ({
    title,
    action,
    confirmLabel,
    onConfirm,
    adviserId,
    centered,
    backdrop,
    ...modalProps
}) => {
    const defaultValues = useMemo(() => ({ actionedById: adviserId, actionDate: new Date() }), []);
    const { control, reset, setValue, handleSubmit } = useForm({ defaultValues })

    const { onHide } = modalProps;
    const onCancel = () => {
        onHide();
        reset(defaultValues);
    }

    const onSubmit = (data) => {
        onHide();
        return onConfirm(data)
            .finally(() => reset(defaultValues));
    }

    return <Modal backdrop="static" centered {...modalProps}>
        <ThemedModal.Header>
            <Modal.Title>{title}</Modal.Title>
        </ThemedModal.Header>
        <form onSubmit={handleSubmit(onSubmit)}>
            <Modal.Body className="d-flex flex-column">
                <Controller
                    control={control}
                    name={'actionedById'}
                    render={({ field: { ref, onChange, value, name, ...rest } }) => <GenericAdviserSelect
                        label={`${action} by:`}
                        defaultValue={value}
                        disableAnimations
                        onChange={(selection) => setValue(name, toNString(selection.value))}
                        {...rest}
                    />}
                />
                <span className="mt-3"><Controller
                    control={control}
                    name={'actionDate'}
                    render={({ field: { ref, onChange, onBlur, value, name, ...rest } }) => <DateTimeInput
                        label={`${action} date:`}
                        value={value}
                        maxDate={new Date()}
                        disableAnimations
                        onBlur={(newDate) => setValue(name, newDate)}
                        {...rest}
                    />}
                /></span>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success" type='submit'>{confirmLabel}</Button>
                <Button variant="light" onClick={onCancel}>Cancel</Button>
            </Modal.Footer>
        </form>
    </Modal>
}

const ClientFactFindPageSubHeader = () => {

    const { account: { adviserId } } = useClientContext();

    const {
        canArchive,
        canOverwriteArchive,
        archiveFactFind,
        isFetching: isFetchingArchiveStatus,
        isArchiving,
        error: archiveError,
        archived,
        archivedInfo,
        refetch: refetchArchiveStatus,
        retryArchive
    } = useArchiveFactFind();
    const {
        canVerify,
        canOverwriteVerification,
        verifyFactFind,
        isFetching: isFetchingVerificationStatus,
        isVerifying,
        error: verifyError,
        verified,
        verifiedInfo,
        refetch: refetchVerificationStatus,
        retryVerify
    } = useVerifyFactFind();
    const {
        showPdfModal,
        pdfData,
        isPreview,
        isFetchingPdf,
        pdfError,
        showPdf,
        hidePdf,
        refetchPdf
    } = useFactFindPdf();

    const disableVerify = useMemo(() => !canVerify || isFetchingVerificationStatus || isVerifying || (verifyError?.allowRetry === false),
        [canVerify, isFetchingVerificationStatus, isVerifying, verifyError?.allowRetry]);

    const [showVerifyModal, setShowVerifyModal] = useState(false);
    const [showArchiveModal, setShowArchiveModal] = useState(false);

    return <>
        <ViewPdfModal
            title={`Fact Find ${isPreview ? "Preview" : "Archive"}`}
            data={pdfData}
            show={showPdfModal}
            handleClose={hidePdf}
            isLoading={isFetchingPdf}
            error={pdfError}
            refetch={refetchPdf}
            backdrop
        />
        <FactFindAdminModal
            title={"Verify Fact Find?"}
            action={"Verified"}
            confirmLabel={"Verify"}
            onConfirm={verifyFactFind}
            adviserId={adviserId}
            show={showVerifyModal}
            onHide={() => setShowVerifyModal(false)}
        />
        <FactFindAdminModal
            title={"Archive Fact Find?"}
            action={"Archived"}
            confirmLabel={"Archive"}
            onConfirm={archiveFactFind}
            adviserId={adviserId}
            show={showArchiveModal}
            onHide={() => setShowArchiveModal(false)}
        />
        <ClientLayoutPageSubHeader>
            <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                    {archiveError?.isFetchingError
                        ? <ErrorMessageWrapper>
                            <ErrorMessage error={archiveError} retryCallback={refetchArchiveStatus} />
                        </ErrorMessageWrapper>
                        : isFetchingArchiveStatus
                            ? <Spinner />
                            : <ButtonGroup>
                                {archived && <Button onClick={showPdf} disabled={isArchiving} variant="info">
                                    <p className="m-0">
                                        View Archive
                                        <br />
                                        {isArchiving || isFetchingArchiveStatus
                                            ? <Spinner />
                                            : <span className="fs-smallest">
                                                {archivedInfo}
                                            </span>}
                                    </p>
                                </Button>}
                                <Button onClick={() => showPdf(true)} variant="success">
                                    Print Snapshot
                                </Button>
                            </ButtonGroup>}
                </div>

                <div>
                    <ButtonGroup>
                        {verifyError != null
                            ? <ErrorMessageWrapper className="me-3">
                                <ErrorMessage imageSrc={null} error={verifyError} retryCallback={verifyError?.allowRetry
                                    ? verifyError?.isFetchingError
                                        ? refetchVerificationStatus
                                        : retryVerify
                                    : null} />
                            </ErrorMessageWrapper>
                            : <FactFindAdminButton
                                hasAccess={canVerify}
                                hasMenuAccess={canOverwriteVerification}
                                onClick={() => verifyFactFind()}
                                onAdviserClick={() => setShowVerifyModal(true)}
                                disabled={disableVerify}
                                variant="success"
                                isFetchingStatus={isFetchingVerificationStatus}
                                isUpdatingStatus={isVerifying}
                                label={verified ? "Verified" : "Verify"}
                                action="Verify"
                                info={verifiedInfo}
                            />}

                        {(archiveError != null && !archiveError.isFetchingError)
                            ? <ErrorMessageWrapper>
                                <ErrorMessage error={archiveError} retryCallback={archiveError?.allowRetry ? retryArchive : null} />
                            </ErrorMessageWrapper>
                            : <FactFindAdminButton
                                hasAccess={canArchive}
                                hasMenuAccess={canOverwriteArchive}
                                onClick={() => archiveFactFind()}
                                onAdviserClick={() => setShowArchiveModal(true)}
                                disabled={isArchiving}
                                variant="primary"
                                isFetchingStatus={isFetchingArchiveStatus}
                                label="Archive"
                                action="Archive"
                            />}
                    </ButtonGroup>
                </div>
            </div>
        </ClientLayoutPageSubHeader>
    </>;
}

export default ClientFactFindPageSubHeader;